import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { MessageService } from '@services/helpers/message-service/message.service';
import {
	BootstrapSettings,
	BootstrapSettingsData,
	CustomizedLabels,
	PanelsPositionObject,
	ServerDataResponse,
	TimeSettingsObject
} from '@models/models';
import { DateTimeSettings } from '@models/settings.model';

@Injectable()
export class SettingsProvider {
	private timeFormatSettingsForSave: TimeSettingsObject;
	private timeFormatSettingsBS: BehaviorSubject<any> = new BehaviorSubject({
		weekStartsOn: null,
		dateFormat: null,
		timeFormat: null,
		fundyIntegration: null,
		proSelectIntegration: null
	});

	private panelsPosition: BehaviorSubject<
		PanelsPositionObject
	> = new BehaviorSubject({
		panelsPosition: null
	});

	private customizeLabels: BehaviorSubject<
		CustomizedLabels
	> = new BehaviorSubject({
		mainShootLabel: null,
		extraShootLabel: null,
		shootsLabel: null,
		businessNumberLabel: null
	});

	constructor(
		public http: HttpClient,
		private messageService: MessageService
	) {}

	getTimezoneList(): Observable<any> {
		const url = '/public/rest/timezones';
		return this.http.get(url);
	}

	getCurrencyList(): Observable<any> {
		const url = '/public/rest/currencies';
		return this.http.get(url);
	}

	getCurrencyAndTaxes(): Observable<any> {
		const url = '/protected/rest/currency_and_taxes';
		return this.http.get(url);
	}

	setDefaultTimeFormatSettings(): any {
		const defaultTimeFormatSettings = {
			weekStartsOn: 'MONDAY',
			dateFormat: 'DD_MM_YYYY',
			timeFormat: 'T_12'
		};
		this.timeFormatSettingsBS.next(defaultTimeFormatSettings);
	}

	getDateTimeSettingsV1(): Observable<ServerDataResponse<DateTimeSettings>> {
		const url = '/api/user/v1/settings/datetime';
		return this.http.get<ServerDataResponse<DateTimeSettings>>(url);
	}

	updateDateTimeSettings(url, data) {
		this.timeFormatSettingsForSave = {
			...this.timeFormatSettingsForSave,
			weekStartsOn: data.weekStartsOn,
			dateFormat: data.dateFormat,
			timeFormat: data.timeFormat
		};
		return this.http.post(url, data);
	}

	getSettingsRequest(): Observable<BootstrapSettings> {
		// this.http
		// 	.post('/api/user/settings/bootstrap/update', {
		// 		name: 'company.settings.xerointegration.version',
		// 		value: 'V2'
		// 	})
		// 	.subscribe();

		const url = '/api/user/settings/bootstrap';
		return this.http
			.get(url)
			.catch(error => {
				this.messageService.showToaster('e_0', 'general', error.error);
				return Observable.throw(error);
			})
			.map(
				(
					data: ServerDataResponse<BootstrapSettingsData>
				): BootstrapSettings => {
					const timeFormatSettings = {
						weekStartsOn: data.data['user.settings.calendar.weekStartsOn'],
						dateFormat: data.data['user.settings.calendar.dateFormat'],
						timeFormat: data.data['user.settings.calendar.timeFormat'],
						fundyIntegration:
							data.data['company.settings.integration.fundy.state'],
						fundyAvailable:
							data.data['company.settings.integration.fundy.available'],
						picTimeAvailable:
							data.data['company.settings.integration.pictime.available'],
						proSelectIntegration:
							data.data['company.settings.integration.proSelect.state'],
						proSelectAvailable:
							data.data['company.settings.integration.proSelect.available'],
						contactFormVersion:
							data.data['company.settings.contactform.version'],
						paymentExportAvailable:
							data.data['company.settings.payments.export.available'],
						leadExportAvailable:
							data.data['company.settings.leads.export.available'],
						jobExportAvailable:
							data.data['company.settings.jobs.export.available'],
						questionnaireTemplatesVersion:
							data.data['company.settings.questionnairebuilder'],
						customisedLabels: data.data['company.settings.customizeLabels'],
						xeroIntegration:
							data.data['company.settings.xerointegration.version'],
						leadSourcesAvailable:
							data.data['company.settings.leadSource.available'],
						qtTemplatesAvailable:
							data.data[
								'company.settings.integration.quoteinvoicesample.available'
							]
					};

					this.setPanelsPosition(
						data.data['company.settings.dashboard.panels.position']
					);

					this.setCustomizeLabels(
						data.data['company.settings.customizeLabels']
					);

					this.timeFormatSettingsBS.next(timeFormatSettings);

					return timeFormatSettings;
				}
			);
	}

	private setPanelsPosition(panelsPosition): void {
		this.panelsPosition.next(panelsPosition);
	}

	get timeFormatSettings(): Observable<BootstrapSettings> {
		return this.timeFormatSettingsBS.asObservable();
	}

	get customizeLabelsObservable(): Observable<CustomizedLabels> {
		return this.customizeLabels.asObservable();
	}

	get panelsPositionObservable() {
		return this.panelsPosition.asObservable();
	}

	setCustomizeLabels(labels: CustomizedLabels): void {
		this.customizeLabels.next(labels);
	}

	savePanelsPosition(panelsPosition) {
		const settingsToSend = [
			{
				name: 'company.settings.dashboard.panels.position',
				jsonValue: {
					panels: panelsPosition
				}
			}
		];
		this.setPanelsPosition({ panels: panelsPosition });

		const url = '/api/user/settings/bootstrap/batch-update';

		return this.http.post(url, settingsToSend);
	}

	saveCustomizedLabels(data: CustomizedLabels): Observable<Object> {
		const settingsToSend = [
			{
				name: 'company.settings.customizeLabels',
				jsonValue: data
			}
		];

		const url = '/api/user/settings/bootstrap/batch-update';
		return this.http.post(url, settingsToSend);
	}

	dateFormatPattern(dateFormat = 'DD_MM_YYYY', weekStartsOn = 'MONDAY') {
		const format = {
			dateFormat,
			timeFormat: 'T_12',
			weekStartsOn
		};
		this.timeFormatSettingsBS.next(format);
	}

	updateFundyIntegration(enableFundy: boolean): Observable<any> {
		const state = enableFundy ? 'ENABLED' : 'DISABLED';
		const url = '/api/user/settings/bootstrap/update ';

		const stateIntegrationToSend = {
			name: 'company.settings.integration.fundy.state',
			value: state
		};

		return this.http
			.post(url, stateIntegrationToSend)
			.catch(error => {
				this.messageService.showToaster('e_0', 'general', error);
				return Observable.throw(error);
			})
			.switchMap(() => this.timeFormatSettings)
			.first()
			.map(settings => {
				settings.fundyIntegration = state;
				this.timeFormatSettingsBS.next(settings);
				return settings;
			});
	}

	updateProSelectIntegration(enableFundy: boolean): Observable<any> {
		const state = enableFundy ? 'ENABLED' : 'DISABLED';
		const url = '/api/user/settings/bootstrap/update ';

		const stateIntegrationToSend = {
			name: 'company.settings.integration.proSelect.state',
			value: state
		};

		return this.http
			.post(url, stateIntegrationToSend)
			.catch(error => {
				this.messageService.showToaster('e_0', 'general', error);
				return Observable.throw(error);
			})
			.switchMap(() => this.timeFormatSettings)
			.first()
			.map(settings => {
				// @ts-ignore
				settings.proSelectIntegration = state;
				this.timeFormatSettingsBS.next(settings);
				return settings;
			});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./shared/services/settings/settings.ts